---
title: "Technologie, die Transformation wirklich trägt: Digitale Zwillinge bei Schunk als Blueprint"
date: "2026-01-18"
excerpt: "Die Fraunhofer-Studie identifiziert „passende technologische Lösungen\" als eine von fünf Kern-Dimensionen erfolgreicher Transformation. Der Einsatz digitaler Zwillinge beim Maschinenbauer Schunk zeigt, wie Technologie vom Pilotprojekt zum skalierbaren Transformationsmotor wird."
metaTitle: "Technologie, die Transformation wirklich trägt: Digitale Zwillinge bei Schunk als Blueprint"
metaDescription: "Die Fraunhofer-Studie identifiziert „passende technologische Lösungen\" als eine von fünf Kern-Dimensionen erfolgreicher Transformation. Der Einsatz digitaler Zwillinge beim Maschinenbauer Schunk zeigt, wie Technologie vom Pilotprojekt zum skalierbaren Transformationsmotor wird."
language: "de"
slug: "digitale-zwillinge-transformation-schunk"
author: "Kevin Rassner"
authorUrl: "https://rassner-dev.org"
canonicalUrl: "https://rassner-dev.org/blog/digitale-zwillinge-transformation-schunk"
publishedOn: "rassner-dev.org"


---

## Technologie als eine von fünf Kern-Dimensionen – richtig verstanden

Im [Fraunhofer-Transformationsindex](/blog/forte-modell-fraunhofer-transformationsindex-31-erfolgsfaktoren) steht „die passenden technologischen Lösungen" neben Führung, Organisation, Anreizen und Partnerschaften.[Handelsblatt 2025] Der Zusatz „passend" ist entscheidend.

Die Auswertung von 250.000 Studien zeigt, dass Technologie alleine wenig bewirkt, wenn sie:
- nur in isolierten Piloten getestet wird,
- nicht in Geschäftsprozesse und Geschäftsmodell integriert ist,
- ohne begleitenden Kompetenzaufbau eingeführt wird.[Handelsblatt 2025]

Das Positivbeispiel Schunk illustriert, wie Technologieeinsatz aussehen kann, wenn er Transformation tatsächlich trägt.

## Schunk: Vom klassischen Maschinenbauer zum KI-getriebenen Lösungsanbieter

Der baden-württembergische Mittelständler Schunk entwickelt mechanische Komponenten wie Greifer und Werkzeughalter, die in der Pharma- ebenso wie in der Autoindustrie eingesetzt werden.[Handelsblatt 2025] Klassischer Maschinenbau – den Schunk durch Innovationen in der Elektronik und Software nun in die Gegenwart übersetzt.

Timo Gessmann, Chief Technology Officer von Schunk, fasst die Strategie zusammen: „Wir bringen unsere mechanischen Kompetenzen jetzt mit Software und Künstlicher Intelligenz zusammen."[Handelsblatt 2025]

### Der Game-Changer: Digitale Zwillinge seit 2020

Der zentrale Hebel ist der Einsatz digitaler Zwillinge. Früher musste Schunk aus seinem Portfolio von 13.000 Komponenten das richtige Produkt finden und zum Kunden bringen, der es dann in sein Werk einbaute und testete.[Handelsblatt 2025] Ein aufwendiger Prozess, der lange dauert.

Mit den digitalen Zwillingen baut Schunk seit 2020 nicht nur seine eigenen Produkte digital nach, sondern auch die Prozesse des Kunden – und kann die Anwendung so digital simulieren, ohne dass Teile physisch hin- und hertransportiert werden.[Handelsblatt 2025]

Gessmann: „Das ist ein Gamechanger. Ich kann sehr schnell aufzeigen, wie ich Prozesse automatisieren kann und wie unsere Greifer und Systeme dabei die Produktivität unserer Kunden steigern."[Handelsblatt 2025]

## Warum Schunk als Positivbeispiel gilt

Das Fraunhofer IAO führt Schunk vor allem deswegen als Positivbeispiel an, weil der Mittelständler seine digitalen Zwillinge nicht nur in bestimmten Teilen der Produktion testet, so wie andere Unternehmen – sondern sie firmenweit eingeführt hat.[Handelsblatt 2025]

Das ist der entscheidende Unterschied: Nicht Pilotitis, sondern Skalierung.

2024 machte das Unternehmen einen Umsatz von 600 Millionen Euro. Das Unternehmen hat inzwischen rund 3700 Beschäftigte weltweit.[Handelsblatt 2025] Diese Wachstumszahlen sind Beleg dafür, dass der Technologieeinsatz nicht nur intern effizienter macht, sondern ein erweitertes Wertversprechen für Kunden schafft.

## Wie Schunk Technologieoffenheit in der Belegschaft schafft

Möglich sind diese Fortschritte bei Schunk auch, weil sich die Angestellten für neue Technologien begeistern. Der digitale Zwilling ist dabei nur ein Beispiel dafür, wie Schunk systematisch mit innovativen Lösungen experimentiert, auch um einen technologieoffenen Mindset im Unternehmen zu schaffen.[Handelsblatt 2025]

### 3D-Drucker als Experimentierraum

Schunk hat zum Beispiel einen 3D-Drucker angeschafft, an dem sich die Beschäftigten ausprobieren können.[Handelsblatt 2025] Gessmann: „Wir ermutigen jeden, sich mit neuen Technologien zu beschäftigen."[Handelsblatt 2025]

Das Ziel sei immer, ein Produkt zu schaffen, das Schunk verkaufen könne. „Aber es geht auch um die Freude daran, diese Innovationen wirklich einzusetzen."[Handelsblatt 2025]

### Vorsicht vor dem „Piloten-Trap"

Gessmann räumt offen ein: „Gehe noch nicht alles mit den digitalen Zwillingen. Aber wir gehen schrittweise vor und sammeln Erfahrungen, durch die wir unser Portfolio anpassen können."[Handelsblatt 2025]

Das ist ein wichtiger Punkt: Schunk vermeidet das klassische Phänomen, bei dem Piloten als Endstation gelten – mit der Logik „haben wir getestet, funktioniert nicht, zurück zur Routine". Stattdessen: Lernen und iterativ verbessern.

## Was daran neu ist – das Verständnis von „passender" Technologie

Der Fraunhofer-Index definiert „passende technologische Lösungen" nicht als „alle Technologie ergreifen". Sondern:

### 1. Technologie als Geschäftsmodell-Erweiterung

Digitale Zwillinge sind nicht nur ein internes Effizienztool, sondern Basis eines erweiterten Wertversprechens: Schunk verkauft nicht mehr nur Komponenten, sondern die Möglichkeit, Prozessoptimierung und Produktivitätsgewinne vorab zu simulieren und zu visualisieren.[Handelsblatt 2025]

Das ist ein kategorialer Unterschied zu Unternehmen, die Technologie nur für interne Kostensenkungg nutzen.

### 2. Skalierung statt Pilotitis

Viele Unternehmen bleiben bei „Leuchtturmprojekten" stecken. Schunk hingegen führt digitale Zwillinge unternehmensweit ein und integriert sie in Standardprozesse.[Handelsblatt 2025] Das entspricht der Fraunhofer-Empfehlung, Technologie flächig in Strukturen zu verankern, anstatt sie als Experiment zu belassen.

### 3. Technologie-Mindset der Belegschaft aktiv entwickeln

Schunk baut nicht allein auf zentrale Expertenteams, sondern schafft Lernräume – etwa durch einen 3D-Drucker, an dem Beschäftigte experimentieren können.[Handelsblatt 2025] Ziel ist, Freude an Technologieeinsatz und praktische Erfahrung breiter im Unternehmen zu verankern.

## Konkrete Takeaways für Organisationsentwickler

### 1. Technologieprojekte an Kundennutzen koppeln

Fragen Sie bei jedem Technologievorhaben:
- Welches konkrete Kundenproblem löst es besser oder schneller?
- Wie kann es zu einem differenzierenden Wertversprechen werden (z. B. schnellere Inbetriebnahme, geringere Ausfallzeiten)?

Digitale Zwillinge sind dafür ein Paradebeispiel: Sie machen Nutzen simulativ sichtbar, bevor investiert wird.[Handelsblatt 2025]

### 2. Piloten früh mit Skalierungspfad denken

Definieren Sie bereits beim Start eines Piloten:
- Welche Prozesse betroffen wären, wenn er skaliert?
- Welche IT- und Datenarchitekturen erforderlich sind?
- Welche Schulungen notwendig werden?

Schunk nutzt digitale Zwillinge nicht als Sonderlösung, sondern baut sie systematisch in Produkt- und Vertriebsprozesse ein.[Handelsblatt 2025]

### 3. Technologieoffenheit in der Belegschaft gezielt fördern

Schaffen Sie „Spielwiesen" für neue Technologien – etwa Labore, Teststände, Sandboxes – mit klar definiertem Zeitbudget.[Handelsblatt 2025] Ziel ist nicht primär sofortiger ROI, sondern Mindset-Shift und Kompetenzaufbau.

### 4. Technologiekompetenz in Linie verankern

Vermeiden Sie, Technologiekompetenz ausschließlich in Zentralfunktionen zu bündeln. Stattdessen:
- Qualifizieren Sie Linienverantwortliche in Grundprinzipien von Daten- und KI-Nutzung.
- Etablieren Sie Schnittstellenrollen (z. B. Product Owner mit hohem Technologieverständnis).

### 5. Messbare Effekte als Storytelling-Grundlage nutzen

Nutzen Sie konkrete Verbesiterungseffekte (z. B. Zeitersparnis durch digitale Zwillinge, Produktivitätszuwächse) für internes Storytelling. Evidenzbasierte Erfolgsgeschichten bauen Ängste ab und unterstützen die Akzeptanz neuer Technologien.[Handelsblatt 2025]

## Operative Umsetzung: Von Technologie-Inseln zur Transformationsplattform

### Schritt 1: Technologielandkarte erstellen

Erfassen Sie alle laufenden und geplanten Technologieinitiativen (KI, Automatisierung, digitale Zwillinge, IoT). Ordnen Sie sie entlang der fünf Fraunhofer-Dimensionen ein: Welche Projekte zahlen auf welche Dimensionen ein, wo gibt es Lücken oder Doppelungen?[Handelsblatt 2025]

### Schritt 2: Priorisierungslogik definieren

Bewerten Sie Initiativen nicht nur nach Einsparpotenzial, sondern nach Transformationswirkung:
- Beitrag zum neuen Geschäftsmodell,
- Skalierbarkeit,
- Hebel für Kultur- und Kompetenzentwicklung.

### Schritt 3: Ein „Flaggschiff"-Projekt für Sichtbarkeit wählen

Wählen Sie ein technologiegestütztes Projekt mit:
- Klar erlebbarer Wirkung für Kunden oder Mitarbeitende,
- Hoher Übertragbarkeit auf andere Bereiche.

Digitale Zwillinge, wie bei Schunk, eignen sich besonders gut, weil Effekte in Simulationen nachvollziehbar werden.[Handelsblatt 2025]

### Schritt 4: Lern- und Feedbackschleifen institutionalisieren

Etablieren Sie Formate, in denen Projektteams Erfahrungen mit neuen Technologien teilen:
- Brown-Bag-Sessions,
- Interne Tech-Days,
- Communities of Practice.

Speisen Sie diese Erkenntnisse systematisch in Ihre Transformationssteuerung ein.

## Die Risikoperspektive: Was passiert, wenn Unternehmen nicht handeln?

Gessmann fasst es prägnant zusammen: „Ich hätte eher Angst, wenn ich nichts machen würde."[Handelsblatt 2025]

Das ist eine wichtige Umkehrung des klassischen Risikodiskurses: Unternehmen fürchten oft, von Technologie überrollt oder ersetzt zu werden. Gessmann kehrt diese Logik um – die größere Gefahr liegt in der Passivität.

## Fazit: Technologie wird zum Transformationsmotor

Die zentrale Botschaft aus Fraunhofer-Studie und Schunk-Fall: Technologie wird erst dann zum Transformationsmotor, wenn sie strategisch eingebettet, skaliert und von der Belegschaft getragen wird – nicht, wenn sie nur als Pilot im Innovationslabor glänzt.[Handelsblatt 2025]

Schunk zeigt, dass der Weg von 13.000 Einzelkomponenten hin zu Prozessoptimierung durch Simulation möglich ist – wenn der Technologieeinsatz mit einem neuen Geschäftsmodell verwoben ist. Die 600 Millionen Euro Umsatz und 3700 Beschäftigte sind das finanzielle Echo dieser Haltung.
